import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchSabaAccess, fetchSabaGames, launchSaba } from '../api/saba';
import { claimWeGaTab, releaseWeGaTab, sendWeGaTab } from './wegaTab';

/**
 * The WeGa shortcuts in the left panel: the first few games of the catalogue as icons, so a player can
 * launch one from any player page instead of going through /games first.
 *
 * Two rows of three (Andrew, 2026-10-23 - four to a row left the icons too small): six icons at most, never a
 * third row and never a scroll bar of its own.
 * A tile that cannot launch (line switched off, empty catalogue, failed read) simply is not shown: the
 * panel has no room for an error state, and the full catalogue under /games still reports its own.
 *
 * The tile loads the vendor's SQUARE logo (`urlLogo`, 301x301 on the live CDN) rather than `urlLogoLarge`
 * (480x560, portrait): this box is square and ~70px, so the square asset fills it with no letterboxing and
 * costs a third less. `/games` keeps the large one - its tiles are about four times the area.
 *
 * The launch itself is Games.js's, unchanged - same one-tab rule for the vendor (components/wegaTab.js),
 * same two failure messages, which every locale already carries.
 */

const COLUMNS = 3;
const ROWS = 2;

// The catalogue does not change while a player is in the app, and this strip is on every player page: one
// read for the life of the page load instead of one per navigation. Failures are cached as an empty list,
// which is what hides the strip - a sidebar that silently retries on every route change is worse.
let catalogueRequest = null;

const loadCatalogue = () => {
  if (!catalogueRequest) {
    catalogueRequest = fetchSabaAccess()
      .then((allowed) => (allowed ? fetchSabaGames() : []))
      .catch((err) => {
        console.log(err);
        return [];
      });
  }
  return catalogueRequest;
};

const GameShortcuts = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [games, setGames] = useState([]);
  const [launching, setLaunching] = useState(null);
  // /games shows the whole catalogue at full size, so the strip would only repeat its first two rows
  const onCatalogue = history.location.pathname === '/games';

  useEffect(() => {
    let live = true;
    loadCatalogue().then((list) => {
      if (live) setGames(Array.isArray(list) ? list : []);
    });
    return () => {
      live = false;
    };
  }, []);

  const shown = games.slice(0, COLUMNS * ROWS);

  const handleLaunch = async (game) => {
    if (launching) return;

    // claimed synchronously in the click, before the request - a tab opened after an await is a popup
    const tab = claimWeGaTab();
    setLaunching(game.id);

    try {
      const url = await launchSaba(game.id);
      if (url) {
        sendWeGaTab(tab, url);
      } else {
        releaseWeGaTab(tab);
        toast.error(t('Unable to launch. Your session may have expired.'), {
          position: 'top-right',
          autoClose: 5000,
        });
      }
    } catch (err) {
      releaseWeGaTab(tab);
      console.log(err);
      toast.error(t('Failed to connect. Please try again.'), {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setLaunching(null);
    }
  };

  if (onCatalogue || shown.length === 0) return null;

  return (
    <div className={'leftside-card'}>
      <div className={'leftside-cardUpper leftside-gamesUpper'}>
        <p className={'leftside-heading'}>
          <span className={`lang-${localStorage.getItem('language')}`}>
            {t('Play exciting games!')}
          </span>
        </p>
      </div>
      <div className={'leftside-cardLower'}>
        <div
          className={'leftside-gamesGrid'}
          style={{ gridTemplateColumns: `repeat(${COLUMNS}, 1fr)` }}
        >
          {shown.map((game) => (
            <div
              key={game.id || game.game}
              className={'leftside-game'}
              onClick={() => handleLaunch(game)}
              title={game.name}
              role="button"
              aria-label={game.name}
            >
              <img
                className={'leftside-gameLogo'}
                src={game.urlLogo || game.urlLogoLarge}
                alt={game.name}
              />
              {launching === game.id ? (
                <div className={'leftside-gameOverlay'}>
                  <img
                    src={require('../img/Icons/preloader.gif')}
                    className={'leftside-gameSpinner'}
                    alt=""
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GameShortcuts;
